import { useState, useEffect } from "react";
import Icon from "../../components/Common/Icon";
import { settingService } from "../../services/settingService";
import { useToast } from "../../context/ToastContext";

export default function SystemSettingsPage() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dynamic Announcement State
  const [announcement, setAnnouncement] = useState({
    text: "",
    type: "INFO",
    active: false,
  });

  // System Rules State
  const [trialDays, setTrialDays] = useState("7");
  const [graceDays, setGraceDays] = useState("3");
  const [inviteHours, setInviteHours] = useState("48");

  // License Months Active/Passive State (1, 3, 6, 12)
  const [activeMonths, setActiveMonths] = useState([1, 3, 6, 12]);

  // Legal Documents State
  const [activeLegalTab, setActiveLegalTab] = useState("terms");
  const [legalDocs, setLegalDocs] = useState({
    terms: "",
    kvkk: "",
    cookies: "",
    agreement: "",
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [annRes, trialRes, graceRes, inviteRes, activeMonthsRes, legalRes] = await Promise.all([
        settingService.getAnnouncement(),
        settingService.getSetting("default_trial_days", "7"),
        settingService.getSetting("grace_period_days", "3"),
        settingService.getSetting("invitation_expiry_hours", "48"),
        settingService.getSetting("active_license_months", "1,3,6,12"),
        settingService.getLegalDocuments(),
      ]);

      if (annRes.data?.data) {
        setAnnouncement(annRes.data.data);
      }
      setTrialDays(trialRes.data?.data || "7");
      setGraceDays(graceRes.data?.data || "3");
      setInviteHours(inviteRes.data?.data || "48");

      const monthsStr = activeMonthsRes.data?.data || "1,3,6,12";
      const parsedMonths = monthsStr
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n > 0);
      setActiveMonths(parsedMonths.length > 0 ? parsedMonths : [1, 3, 6, 12]);

      if (legalRes.data?.data) {
        setLegalDocs(legalRes.data.data);
      }
    } catch (err) {
      console.error("Sistem ayarları yüklenemedi:", err);
      showToast("Sistem ayarları yüklenemedi.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingService.updateAnnouncement(announcement);
      showToast("Sistem duyurusu ve banner ayarları başarıyla güncellendi!", "success");
    } catch (err) {
      showToast(err.response?.data?.error ?? "Duyuru güncellenemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRules = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const sortedMonths = [...activeMonths].sort((a, b) => a - b).join(",");
      await Promise.all([
        settingService.setSetting("default_trial_days", trialDays),
        settingService.setSetting("grace_period_days", graceDays),
        settingService.setSetting("invitation_expiry_hours", inviteHours),
        settingService.setSetting("active_license_months", sortedMonths),
      ]);
      showToast("Sistem parametreleri ve lisans süre seçenekleri başarıyla güncellendi!", "success");
    } catch (err) {
      showToast(err.response?.data?.error ?? "Parametreler güncellenemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleMonthOption = (m) => {
    setActiveMonths((prev) => {
      if (prev.includes(m)) {
        if (prev.length <= 1) {
          showToast("En az bir lisans süresi aktif kalmalıdır.", "warning");
          return prev;
        }
        return prev.filter((x) => x !== m);
      } else {
        return [...prev, m];
      }
    });
  };

  const handleSaveLegalDocs = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingService.updateLegalDocuments(legalDocs);
      showToast("Yasal metinler ve politikalar başarıyla güncellendi!", "success");
    } catch (err) {
      showToast(err.response?.data?.error ?? "Yasal metinler güncellenemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
        Sistem ayarları yükleniyor…
      </div>
    );
  }

  const ANNOUNCEMENT_PREVIEW_STYLES = {
    INFO: "bg-info/10 border-info/20 text-info",
    WARNING: "bg-amber-500/10 border-amber-500/30 text-amber-700",
    DANGER: "bg-error/10 border-error/20 text-error",
  };

  const ANNOUNCEMENT_ICONS = {
    INFO: "info",
    WARNING: "warning",
    DANGER: "error",
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-12">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-on-surface flex items-center gap-2.5">
          <Icon name="tune" className="text-primary !text-[28px]" />
          Sistem Duyuru Ayarları
        </h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Tüm salonlar için geçerli olan sistem kurallarını, deneme sürelerini ve canlı duyuru banner'ını kod değiştirmeden yönetin.
        </p>
      </div>

      {/* Section 1: Dinamik Sistem Duyurusu & Banner */}
      <form onSubmit={handleSaveAnnouncement} className="bg-white rounded-3xl p-6 sm:p-8 border border-outline-variant/40 shadow-sm space-y-6">
        <div className="flex items-center justify-between border-b border-outline-variant/30 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon name="campaign" filled className="!text-[22px]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-on-surface">Canlı Sistem Duyurusu (Banner)</h2>
              <p className="text-xs text-on-surface-variant">
                Tüm salonların ana ekranlarında (Web & Mobil) anında görünecek duyuru oluşturun.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <span className="text-sm font-semibold text-on-surface-variant">
              {announcement.active ? "Yayında" : "Yayında Değil"}
            </span>
            <input
              type="checkbox"
              checked={announcement.active}
              onChange={(e) => setAnnouncement((prev) => ({ ...prev, active: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-outline-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary relative"></div>
          </label>
        </div>

        {/* Duyuru Tipi Seçimi */}
        <div>
          <label className="block text-sm font-medium mb-2 text-on-surface-variant">Duyuru Tipi</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { type: "INFO", label: "Bilgilendirme", icon: "info", color: "border-info/40 text-info bg-info/5" },
              { type: "WARNING", label: "Uyarı", icon: "warning", color: "border-amber-500/40 text-amber-700 bg-amber-500/5" },
              { type: "DANGER", label: "Kritik", icon: "error", color: "border-error/40 text-error bg-error/5" },
            ].map((item) => {
              const selected = announcement.type === item.type;
              return (
                <button
                  type="button"
                  key={item.type}
                  onClick={() => setAnnouncement((prev) => ({ ...prev, type: item.type }))}
                  className={`p-3.5 rounded-2xl border flex items-center justify-center gap-2.5 font-semibold text-sm transition-all duration-200 ${
                    selected
                      ? `${item.color} ring-2 ring-primary/20 shadow-sm`
                      : "border-outline-variant text-on-surface-variant hover:bg-surface-card"
                  }`}
                >
                  <Icon name={item.icon} filled className="!text-[20px]" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Duyuru Metni */}
        <div>
          <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">Duyuru Metni</label>
          <textarea
            rows={3}
            value={announcement.text}
            onChange={(e) => setAnnouncement((prev) => ({ ...prev, text: e.target.value }))}
            placeholder="Örn: 25 Temmuz saat 02:00'de planlı veritabanı bakımı yapılacaktır."
            className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm text-on-surface"
          />
        </div>

        {/* Canlı Önizleme Box */}
        <div>
          <span className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
            Canlı Önizleme (Dashboard Görünümü)
          </span>
          {announcement.active && announcement.text.trim() ? (
            <div className={`border rounded-3xl p-5 flex items-start gap-4 shadow-sm ${ANNOUNCEMENT_PREVIEW_STYLES[announcement.type]}`}>
              <div className="w-10 h-10 rounded-2xl bg-current/10 flex items-center justify-center flex-shrink-0">
                <Icon name={ANNOUNCEMENT_ICONS[announcement.type]} filled className="!text-[22px]" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-base">
                  {announcement.type === "INFO" && "Sistem Bilgilendirmesi"}
                  {announcement.type === "WARNING" && "Sistem Uyarısı"}
                  {announcement.type === "DANGER" && "Önemli Sistem Duyurusu"}
                </h4>
                <p className="text-sm mt-1 font-medium leading-relaxed opacity-90">{announcement.text}</p>
              </div>
            </div>
          ) : (
            <div className="bg-surface-card border border-dashed border-outline-variant rounded-2xl p-4 text-center text-xs text-on-surface-variant">
              Duyuru yayında değil veya metin girilmemiş.
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl text-sm transition shadow-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Icon name="save" className="!text-[18px]" />
            Duyuru Ayarlarını Kaydet
          </button>
        </div>
      </form>

      {/* Section 2: Sistem Kuralları ve Parametreleri */}
      <form onSubmit={handleSaveRules} className="bg-white rounded-3xl p-6 sm:p-8 border border-outline-variant/40 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-outline-variant/30 pb-4">
          <div className="w-10 h-10 rounded-2xl bg-tier-gold/10 text-tier-gold flex items-center justify-center">
            <Icon name="settings" filled className="!text-[22px]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-on-surface">Varsayılan Sistem Parametreleri</h2>
            <p className="text-xs text-on-surface-variant">
              Salon onboarding ve lisans süreçlerinde kullanılan dinamik değişkenler.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">
              Varsayılan Deneme Süresi (Gün)
            </label>
            <input
              type="number"
              min="1"
              max="90"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm font-medium"
            />
            <span className="text-[11px] text-on-surface-variant mt-1 block">
              Onboarding sırasında 0 ay seçilince verilecek gün sayısı.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">
              Tolerans (Grace) Süresi (Gün)
            </label>
            <input
              type="number"
              min="0"
              max="30"
              value={graceDays}
              onChange={(e) => setGraceDays(e.target.value)}
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm font-medium"
            />
            <span className="text-[11px] text-on-surface-variant mt-1 block">
              Lisans bittikten sonraki salt-okunur erişim süresi.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">
              Davet Geçerlilik Süresi (Saat)
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={inviteHours}
              onChange={(e) => setInviteHours(e.target.value)}
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm font-medium"
            />
            <span className="text-[11px] text-on-surface-variant mt-1 block">
              Aktivasyon e-posta linkinin aktif kalma süresi.
            </span>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-on-surface text-white hover:bg-black font-semibold rounded-xl text-sm transition shadow-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Icon name="save" className="!text-[18px]" />
            Parametreleri Kaydet
          </button>
        </div>
      </form>

      {/* Section 3: Dinamik Lisans Süre Seçenekleri (1, 3, 6, 12 Aylık Yönetimi) */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-outline-variant/40 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-outline-variant/30 pb-4">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="calendar_month" filled className="!text-[22px]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-on-surface">Lisans Süre Seçenekleri Yönetimi</h2>
            <p className="text-xs text-on-surface-variant">
              Tüm sistemdeki salonların tercih edebileceği abonelik sürelerini (1, 3, 6, 12 Aylık) dinamik olarak aktif veya pasife alın.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { months: 1, label: "1 Aylık Lisans", subtext: "Aylık Standart Ödeme" },
            { months: 3, label: "3 Aylık Lisans", subtext: "Çeyreklik Dönem" },
            { months: 6, label: "6 Aylık Lisans", subtext: "Yarı Yıl Paket" },
            { months: 12, label: "12 Aylık (1 Yıl)", subtext: "Yıllık Taahhütlü" },
          ].map((opt) => {
            const isActive = activeMonths.includes(opt.months);
            return (
              <div
                key={opt.months}
                onClick={() => toggleMonthOption(opt.months)}
                className={`cursor-pointer p-4 rounded-2xl border-2 transition-all flex flex-col justify-between ${
                  isActive
                    ? "border-emerald-500/40 bg-emerald-500/5 shadow-sm"
                    : "border-rose-200 bg-rose-50/50"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm text-on-surface">{opt.label}</span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${
                      isActive
                        ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
                        : "bg-rose-500/15 text-rose-700 border-rose-500/30"
                    }`}
                  >
                    <Icon name={isActive ? "check_circle" : "block"} className="!text-[12px]" />
                    {isActive ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <p className="text-xs text-on-surface-variant mb-3">{opt.subtext}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMonthOption(opt.months);
                  }}
                  className={`w-full py-1.5 rounded-xl text-xs font-semibold transition cursor-pointer ${
                    isActive
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-rose-600 text-white hover:bg-rose-700"
                  }`}
                >
                  {isActive ? "Pasife Al (Kapat)" : "Aktif Yap (Aç)"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-outline-variant/30">
          <p className="text-xs text-on-surface-variant italic">
            * Pasife aldığınız süre seçeneği ("Paketim & Lisansım" ve Onboarding ekranlarında) anında devre dışı bırakılır.
          </p>
          <button
            type="button"
            onClick={handleSaveRules}
            disabled={saving}
            className="px-6 py-2.5 bg-primary text-white hover:bg-primary-hover font-semibold rounded-xl text-sm transition shadow-sm flex items-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            <Icon name="save" className="!text-[18px]" />
            Süre Seçeneklerini Kaydet
          </button>
        </div>
      </div>

      {/* Section 4: Dinamik Yasal Metinler & Politika Yönetimi */}
      <form onSubmit={handleSaveLegalDocs} className="bg-white rounded-3xl p-6 sm:p-8 border border-outline-variant/40 shadow-sm space-y-6">
        <div className="flex items-center gap-3 border-b border-outline-variant/30 pb-4">
          <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
            <Icon name="gavel" filled className="!text-[22px]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-on-surface">Yasal Metinler & Politika Yönetimi</h2>
            <p className="text-xs text-on-surface-variant">
              Giriş ekranında ve mobil uygulamada gösterilen yasal metinleri (KVKK, Kullanım Şartları, Çerez Politikası, Hizmet Sözleşmesi) kod değiştirmeden düzenleyin.
            </p>
          </div>
        </div>

        {/* Legal Tabs */}
        <div className="flex border-b border-outline-variant/30 gap-2 overflow-x-auto pb-1">
          {[
            { id: "terms", label: "Kullanım Koşulları", icon: "gavel" },
            { id: "kvkk", label: "KVKK Aydınlatma Metni", icon: "shield" },
            { id: "cookies", label: "Çerez Politikası", icon: "cookie" },
            { id: "agreement", label: "Mesafeli Hizmet Sözleşmesi", icon: "description" },
          ].map((tab) => {
            const active = activeLegalTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveLegalTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold rounded-xl transition whitespace-nowrap ${
                  active
                    ? "bg-primary text-white shadow-sm"
                    : "bg-surface-subtle text-on-surface-variant hover:bg-outline-variant/20 hover:text-on-surface"
                }`}
              >
                <Icon name={tab.icon} className="!text-[16px]" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Text Area */}
        <div>
          <label className="block text-xs font-bold mb-2 text-on-surface-variant">
            Metin İçeriği (Markdown başlıkları #, ## ve liste maddeleri - şeklinde kullanılabilir)
          </label>
          <textarea
            rows={12}
            value={legalDocs[activeLegalTab] || ""}
            onChange={(e) =>
              setLegalDocs((prev) => ({
                ...prev,
                [activeLegalTab]: e.target.value,
              }))
            }
            className="w-full border border-outline-variant rounded-2xl p-4 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm font-mono leading-relaxed bg-surface-subtle/30"
            placeholder="Yasal metin içeriğini buraya giriniz..."
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-outline-variant/30">
          <span className="text-xs text-on-surface-variant italic">
            * Değişiklik yaptığınızda Giriş ekranı ve Mobil uygulama anında güncellenir.
          </span>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm transition shadow-sm flex items-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            <Icon name="save" className="!text-[18px]" />
            Yasal Metinleri Kaydet
          </button>
        </div>
      </form>
    </div>
  );
}
